import React, { useState, useEffect, useRef } from "react";

import ModalHolder from "../../molecules/ModalHolder";

import { toastWarning } from "../../../shared/helpers";

import { H3, H5, P } from "../../atoms/Texts";
import { Badge, PrimaryButton } from "../../atoms/Buttons";
import { useForm, Controller } from "react-hook-form";

import {
  CustomSelectDropdown,
  DarkFormField,
  CustomCheckbox,
} from "../../atoms/Inputs";

import { selectStyle } from "../../../shared/fixtures/select_style";

const AcceptReferralModal = ({ closeModal, referral }) => {
  const {
    control,
    register,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      generateCaseId: true,
    },
  });

  const generateCaseId = watch("generateCaseId");

  const { user, from, serviceName } = referral;

  const [badgeColor, setBadgeColor] = useState({});

  React.useEffect(() => {
    switch (true) {
      case from === "form":
        setBadgeColor({
          bgColor: "#FDD2DC",
          textColor: "#7C033C",
        });
        break;

      case from === "sdep":
        setBadgeColor({
          bgColor: "#D8FDEC",
          textColor: "#167C3C",
        });
        break;

      default:
        setBadgeColor({
          bgColor: "#DCD6FE",
          textColor: "#2220A9",
        });
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const teamMembers = [
    {
      label: "John Doe",
      value: "12345566778",
    },
    {
      label: "Clarrisa Joe",
      value: "12345566779",
    },
    {
      label: "Terrance Lay",
      value: "12345566770",
    },
  ];

  const onacceptOrRejectReferral = () => {
    toastWarning("Cannot perform this action on Demo mode");
  };

  return (
    <ModalHolder closeModal={closeModal}>
      <div className="pb-12">
        <H3 color="#22202D" fontFamily="medium" className="text-center">
          Accept Case
        </H3>
        <P
          fontFamily="regular"
          color="#5C5C5C"
          fontSize="14px"
          className="text-center md:w-3/4 mx-auto mt-2 mb-12"
        >
          By accepting, you open a case for this referral. This will occupy one
          serving capacity on {serviceName}
        </P>

        <div className="my-6 flex items-center gap-x-2 justify-between">
          <div className="flex items-center gap-x-4">
            <div
              className=" w-16 h-16 flex  items-center justify-center rounded-lg mb-3"
              style={{ background: "#EAE7FE" }}
            >
              <H5 color="#12046D" className="capitalize profile-dropdown-child">
                {`${user?.firstName?.[0]} ${user?.lastName?.[0]}`}
              </H5>
            </div>

            <div>
              <H5 color="#716E8B" fontSize="16px" fontFamily="semi" smallSize="16px">
                {`${referral?.user?.firstName} ${referral?.user?.lastName}`}
              </H5>
              <P
                color="#716E8B"
                fontSize="13px"
                fontFamily="regular"
                smallSize="13px"
                className="my-0 text-center"
              >
                {user?.email}
              </P>
            </div>
          </div>

          <div className="">
            <Badge
              fontFamily="medium"
              bg={badgeColor.bgColor}
              color={badgeColor.textColor}
              className="uppercase"
              fontSize="12px"
            >
              {from}
            </Badge>
          </div>
        </div>

        <form className="mb-6">
          <div className=" flex items-center gap-x-2">
            <Controller
              name="generateCaseId"
              control={control}
              render={({ field }) => (
                <CustomCheckbox
                  id="agenerateCaseId"
                  label=""
                  checked={field.value}
                  onChange={field.onChange}
                  name={field.name}
                  ref={field.ref}
                />
              )}
            />

            <label className="block">
              <P fontSize="14px" fontFamily="regular" color="#413E53">
                Auto-generate Case ID
              </P>
            </label>

            <div className="flex-1">
              <InfoTooltip description="If Auto-generate Case ID is selected, the system will automatically create a unique case ID for you." />
            </div>
          </div>

          {!generateCaseId && (
            <div className="mt-6">
              <DarkFormField
                type="text"
                id="caseNumber"
                name="caseNumber"
                {...register("caseNumber", {
                  required: "Please enter your first name",
                  minLength: {
                    value: 2,
                    message: "Enter a case number",
                  },
                })}
                placeholder="e.g AD-411535FR"
                label="Case ID"
                className="w-full"
              />
              {errors.caseNumber && (
                <P fontSize="12px" className="-mt-4 mb-4 text-red-400 text-sm">
                  {errors.caseNumber.message}
                </P>
              )}
            </div>
          )}

          <div className="mt-6">
            <label>
              <P
                fontSize="14px"
                fontFamily="medium"
                color="#47495C"
                className="mb-2"
              >
                Assign Team Member (optional)
              </P>
            </label>

            <Controller
              name={"assignedMemberIds"}
              control={control}
              rules={{
                required: "Please select an option",
              }}
              render={({ field }) => (
                <CustomSelectDropdown
                  {...field}
                  placeholder="Select a team member"
                  control={control}
                  styles={selectStyle}
                  // isLoading={teamLoaading}
                  options={teamMembers}
                  isMulti={true}
                />
              )}
            />

            {errors.assignedMemberIds && (
              <P fontSize="12px" className="-mt-4 mb-4 text-red-400 text-sm">
                {errors.assignedMemberIds.message}
              </P>
            )}
          </div>
        </form>

        <div className="flex gap-x-4">
          <PrimaryButton.Light type="button" onClick={closeModal} className="w-1/2">
            Cancel
          </PrimaryButton.Light>
          <PrimaryButton
            className="w-full "
            onClick={() => onacceptOrRejectReferral("approved")}
          >
            Confirm
          </PrimaryButton>
        </div>
      </div>
    </ModalHolder>
  );
};

export default AcceptReferralModal;

export const InfoTooltip = ({ title, description, direction = "left" }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const tooltipRef = useRef(null);

  const handleMouseEnter = () => {
    setShowTooltip(true);
  };

  const handleMouseLeave = () => {
    setShowTooltip(false);
  };

  // Close tooltip when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target)) {
        setShowTooltip(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className="relative" ref={tooltipRef}>
      <div
        className="w-8 h-8 rounded-full flex items-center justify-center cursor-pointer"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <svg
          width="19"
          height="18"
          viewBox="0 0 19 18"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M9.64453 16.5C13.7867 16.5 17.1445 13.1421 17.1445 9C17.1445 4.85786 13.7867 1.5 9.64453 1.5C5.5024 1.5 2.14453 4.85786 2.14453 9C2.14453 13.1421 5.5024 16.5 9.64453 16.5Z"
            stroke="#413E53"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M7.46191 6.74945C7.63824 6.2482 7.98628 5.82553 8.44438 5.5563C8.90248 5.28707 9.44109 5.18865 9.9648 5.27848C10.4885 5.36831 10.9635 5.64059 11.3057 6.0471C11.6479 6.4536 11.8352 6.96809 11.8344 7.49945C11.8344 8.99945 9.58441 9.74945 9.58441 9.74945"
            stroke="#413E53"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M9.64453 12.75H9.65203"
            stroke="#413E53"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
      {/* AD-411535FR */}
      {showTooltip && (
        <div
          style={{
            background: "#442ED0",
            padding: "12px 10px",
            left: "-120px",
          }}
          className={`absolute  top-10 z-50 w-52  rounded-lg  shadow-md`}
        >
          <div>
            {title && (
              <h4 className="text-base font-bold text-white mb-1">{title}</h4>
            )}
            <p className="text-xs font-medium text-white whitespace-normal">
              {description}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};
